import { TargetSchema, type ExtractedRecord } from "@/types";
import { getRecordData } from "./recordUtils";
import { parseMoneyCell } from "./amounts";
import { absoluteIndexOf } from "./recordIndexing";

/**
 * Reported YTD vs claim lines (#7432 AC5).
 *
 * A carrier file that carries both a per-claimant reported YTD and the claim
 * lines behind it lets the reviewer check one against the other before
 * approving. A gap usually means claim lines are missing from the file,
 * duplicated, or the wrong paid column is mapped — all things to catch before
 * the reported total becomes the claimant's official figure (ADR-051).
 *
 * Computed in the browser over the LOADED records: reported from the
 * claimant row's ytd_total_paid, lines from the paid_amount of its linked
 * claim_item rows that the reviewer has not excluded. The caller says whether
 * the loaded set is the whole file; on a paged document the check covers the
 * loaded claimants and the rail says so.
 */
export interface YtdMismatch {
  /** Absolute record_index of the claimant row — what grid filters key on. */
  recordIndex: number;
  name: string;
  reported: number;
  lines: number;
  /** lines − reported: positive when the claim lines add up to MORE than the carrier reported. */
  diff: number;
}

export interface YtdReconciliation {
  /**
   * False when the check does not apply: no claimant row reports a YTD, or
   * none has claim lines to compare it with. The rail then shows no YTD line
   * at all rather than a vacuous "matches for all 0".
   */
  available: boolean;
  /** Claimants that had both a reported YTD and at least one claim line. */
  claimantsChecked: number;
  mismatches: YtdMismatch[];
  /** The mismatch with the largest absolute gap, for the card's "Largest gap" line. */
  largest: YtdMismatch | null;
}

/** Cents-level tolerance: carrier sheets round, and $0.004 is not a reconciliation finding. */
const TOLERANCE = 0.005;

const nameOf = (claimant: ExtractedRecord): string => {
  const d = getRecordData(claimant, TargetSchema.CLAIMANT);
  const full = d.claimant_name ?? [d.claimant_first_name, d.claimant_last_name].filter(Boolean).join(" ");
  return full ? String(full) : "Unnamed claimant";
};

export const reconcileReportedYtd = ({
  claimants,
  linesOf,
  isExcluded,
}: {
  claimants: ExtractedRecord[];
  /** Linked records for a claimant row (claim lines live in CLAIM_ITEM-schema records). */
  linesOf: (claimant: ExtractedRecord) => ExtractedRecord[];
  isExcluded: (record: ExtractedRecord) => boolean;
}): YtdReconciliation => {
  const mismatches: YtdMismatch[] = [];
  let claimantsChecked = 0;
  for (const claimant of claimants) {
    if (isExcluded(claimant)) continue;
    const reported = parseMoneyCell(getRecordData(claimant, TargetSchema.CLAIMANT).ytd_total_paid);
    if (reported === null) continue;
    const lines = linesOf(claimant).filter(
      (r) => !isExcluded(r) && (r.extracted_fields ?? []).some((f) => f.target_schema === TargetSchema.CLAIM_ITEM)
    );
    if (lines.length === 0) continue;
    claimantsChecked += 1;
    let sum = 0;
    for (const line of lines) {
      const paid = parseMoneyCell(getRecordData(line, TargetSchema.CLAIM_ITEM).paid_amount);
      if (paid !== null) sum += paid;
    }
    const diff = sum - reported;
    if (Math.abs(diff) > TOLERANCE) {
      const recordIndex = absoluteIndexOf(claimant);
      if (recordIndex === undefined) continue;
      mismatches.push({ recordIndex, name: nameOf(claimant), reported, lines: sum, diff });
    }
  }
  const largest = mismatches.reduce<YtdMismatch | null>(
    (best, m) => (best === null || Math.abs(m.diff) > Math.abs(best.diff) ? m : best),
    null
  );
  return { available: claimantsChecked > 0, claimantsChecked, mismatches, largest };
};
